import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Question, SkillTest, SkillTestDraft, SkillTestStatus } from '../types/skillTests';
import { createSkillTestApi, deleteSkillTestApi, fetchSkillTests, updateSkillTestApi } from '../api/skillTestsApi';

interface DeletedSkillTest {
  test: SkillTest;
  index: number;
}

interface SkillTestsContextValue {
  tests: SkillTest[];
  loading: boolean;
  error: string | null;
  getTestById: (id: number | string) => SkillTest | undefined;
  createTest: (draft: SkillTestDraft) => Promise<SkillTest>;
  updateTest: (id: number | string, draft: SkillTestDraft) => Promise<SkillTest | undefined>;
  deleteTest: (id: number | string) => Promise<DeletedSkillTest | undefined>;
  restoreTest: (payload: DeletedSkillTest) => void;
  duplicateTest: (id: number | string) => SkillTest | undefined;
  toggleStatus: (id: number | string) => SkillTest | undefined;
}

const SkillTestsContext = createContext<SkillTestsContextValue | null>(null);

const cloneQuestions = (questions: Question[]): Question[] =>
  questions.map((question) => ({
    ...question,
    options: question.options ? [...question.options] : undefined,
  }));

const today = () => new Date().toISOString().split('T')[0];

export function SkillTestsProvider({ children }: { children: ReactNode }) {
  const [tests, setTests] = useState<SkillTest[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!import.meta.env.VITE_API_URL) {
      setError('VITE_API_URL is required for skill tests.');
      setLoading(false);
      return;
    }
    void (async () => {
      try {
        const remote = await fetchSkillTests();
        setTests(remote);
      } catch (err) {
        console.error(err);
        setError(err instanceof Error ? err.message : 'Unable to load skill tests');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const getTestById = (id: number | string) => tests.find((test) => String(test.id) === String(id));

  const createTest = async (draft: SkillTestDraft): Promise<SkillTest> => {
    const created = await createSkillTestApi(draft);
    setTests((prev) => [...prev, created]);
    return created;
  };

  const updateTest = async (id: number | string, draft: SkillTestDraft): Promise<SkillTest | undefined> => {
    const updated = await updateSkillTestApi(String(id), draft);
    setTests((prev) => prev.map((test) => (test.id === updated.id ? updated : test)));
    return updated;
  };

  const deleteTest = async (id: number | string): Promise<DeletedSkillTest | undefined> => {
    await deleteSkillTestApi(String(id));
    const index = tests.findIndex((test) => String(test.id) === String(id));
    if (index === -1) {
      return undefined;
    }
    const removedTest = tests[index];
    setTests((prev) => prev.filter((test) => String(test.id) !== String(id)));
    return { test: removedTest, index };
  };

  const duplicateTest = (id: number | string) => {
    const source = getTestById(id);
    if (!source) {
      return undefined;
    }
    const duplicated: SkillTest = {
      ...source,
      id: Date.now(),
      title: `${source.title} (Copy)`,
      createdDate: today(),
      lastModified: today(),
      timesUsed: 0,
      status: 'draft',
      questions: cloneQuestions(source.questions),
    };
    setTests((prev) => [...prev, duplicated]);
    return duplicated;
  };

  const toggleStatus = (id: number | string) => {
    let updated: SkillTest | undefined;
    setTests((prev) =>
      prev.map((test) => {
        if (String(test.id) !== String(id)) {
          return test;
        }
        const nextStatus: SkillTestStatus =
          test.status === 'active' ? 'draft' : test.status === 'draft' ? 'active' : test.status;
        updated = {
          ...test,
          status: nextStatus,
          lastModified: today(),
        };
        return updated!;
      }),
    );
    return updated;
  };

  const value: SkillTestsContextValue = {
    tests,
    loading,
    error,
    getTestById,
    createTest,
    updateTest,
    deleteTest,
    restoreTest: ({ test, index }) => {
      setTests((prev) => {
        if (prev.some((existing) => existing.id === test.id)) {
          return prev;
        }
        const next = [...prev];
        next.splice(Math.min(index, next.length), 0, { ...test, questions: cloneQuestions(test.questions) });
        return next;
      });
    },
    duplicateTest,
    toggleStatus,
  };

  return <SkillTestsContext.Provider value={value}>{children}</SkillTestsContext.Provider>;
}

export function useSkillTests() {
  const context = useContext(SkillTestsContext);
  if (!context) {
    throw new Error('useSkillTests must be used within a SkillTestsProvider');
  }
  return context;
}
